import React from 'react';
import { getViridisGradientStops } from '../../../../../utils/renewableUtils';

// Card style for the floating-over-the-map legend (loading + discrete percentile).
const OVERLAY_CARD_CLASSES =
  'absolute bottom-4 right-4 z-[1000] pointer-events-auto select-none bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs';

// Card style for the inline legend (continuous clustered C.F bar) rendered next to the stats row.
const INLINE_CARD_CLASSES = 'select-none bg-white rounded-lg p-2';

/**
 * Solar Capacity Factor legend.
 *
 * placement="overlay" → renders inside the map's relative container, absolute-positioned.
 *                       Used for the loading message and the discrete (percentile) bullet list.
 * placement="inline"  → renders alongside the stats row.
 *                       Used only for the continuous (clustered) viridis bar.
 *
 * Each placement returns null when the current mode does not belong to it, so the
 * parent card can mount the component twice (once in each slot) without duplicates.
 */
const SolarCapacityFactorLegendPanel = ({ solarThresholds, placement = 'inline' }) => {
  const isLoading = !solarThresholds;
  const isContinuous = solarThresholds?.mode === 'continuous';

  if (placement === 'inline') {
    if (!isContinuous) return null;

    const vmin = solarThresholds.vmin;
    const vmax = solarThresholds.vmax;
    const gradient = getViridisGradientStops(14);

    return (
      <div className={INLINE_CARD_CLASSES}>
        <div className="text-[11px] font-semibold text-gray-800 mb-1 text-center leading-none">
          ☀️ C.F
        </div>
        <div className="flex flex-row items-center gap-1.5">
          <div className="text-[10px] text-gray-700 leading-none whitespace-nowrap">
            {(vmin * 100).toFixed(2)}%
          </div>
          <div
            className="rounded-sm flex-1"
            style={{
              minWidth: 110,
              height: 12,
              background: `linear-gradient(to right, ${gradient})`,
              border: '1px solid rgba(0,0,0,0.12)'
            }}
          />
          <div className="text-[10px] text-gray-700 leading-none whitespace-nowrap">
            {(vmax * 100).toFixed(2)}%
          </div>
        </div>
      </div>
    );
  }

  // placement === 'overlay'
  if (isLoading) {
    return (
      <div className={OVERLAY_CARD_CLASSES}>
        <div className="text-xs text-gray-600">
          <span>Calculating thresholds...</span>
        </div>
      </div>
    );
  }

  if (isContinuous) return null;

  return (
    <div className={OVERLAY_CARD_CLASSES}>
      <div className="space-y-1">
        <div className="text-xs font-semibold text-gray-700 mb-2 border-b border-gray-200 pb-1">
          ☀️ Solar Capacity Factor
        </div>
        <div className="flex items-center text-xs">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: solarThresholds.excellent.color }}
          ></div>
          <span>Excellent: {(solarThresholds.excellent.threshold * 100).toFixed(1)}%+</span>
        </div>
        <div className="flex items-center text-xs">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: solarThresholds.high.color }}
          ></div>
          <span>
            High: {(solarThresholds.high.threshold * 100).toFixed(1)}-{(solarThresholds.excellent.threshold * 100).toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center text-xs">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: solarThresholds.good.color }}
          ></div>
          <span>
            Good: {(solarThresholds.good.threshold * 100).toFixed(1)}-{(solarThresholds.high.threshold * 100).toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center text-xs">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: solarThresholds.fair.color }}
          ></div>
          <span>
            Fair: {(solarThresholds.fair.threshold * 100).toFixed(1)}-{(solarThresholds.good.threshold * 100).toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center text-xs">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: solarThresholds.poor.color }}
          ></div>
          <span>Poor: &lt;{(solarThresholds.fair.threshold * 100).toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
};

export default SolarCapacityFactorLegendPanel;
